import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Search } from 'lucide-react';
import { PrimaryButton, AgTableCard, Input } from "../UI";
import InlineTooltip from '../UI/Tooltip/InlineTooltip';
import DateRangePicker from '../UI/DateRangePicker';
import { CSVLink } from 'react-csv';
import { useUserContext } from '../../contexts/UserContext';
import { useContentfulContext } from '../../contexts/ContentfulContext';
import { getAnalyticsEventsData, getProductAnalyticsData } from '../../utils/APIs';


const DASHBOARD_CARDS = [
    { label: 'EXPERIENCES', width: 'half', count_header: 'Count', help_text: 'Breakdown of experiences created, approved, and activated.' },
    { label: 'USER LOGINS', width: 'half', count_header: 'Total Logins', help_text: 'Users with the most logins.' },
    { label: 'MOST USED FUNCTIONS', width: 'full', count_header: 'Executions', help_text: 'The most frequently executed functions.' },
];

const CARD_COLORS = ['#F5A623', '#4A90D9', '#7B61FF', '#E74C3C', '#2ECC71', '#9B59B6'];

const LoadingShimmer = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full mx-auto">
        {DASHBOARD_CARDS.map((config, idx) => {
            const colSpanClass = config.width === 'full' ? 'md:col-span-2' : 'md:col-span-1';
            return (
                <div key={idx} className={`border border-gray-200 rounded-lg p-5 animate-pulse ${colSpanClass}`}>
                    <div className="h-5 bg-gray-200 rounded w-2/3 mb-4" />
                    {Array.from({ length: 5 }).map((__, i) => (
                        <div key={i} className="flex items-center gap-3 mb-3">
                            <div className="h-4 bg-gray-200 rounded w-6" />
                            <div className="h-4 bg-gray-200 rounded flex-1" />
                            <div className="h-4 bg-gray-200 rounded w-10" />
                        </div>
                    ))}
                </div>
            );
        })}
    </div>
);

const CardGrid = ({ cardsData, noDataMsg, noDataAvailable, viewDetailsBtn, totalRecordsText, onViewDetails }) => {
    if (!cardsData || cardsData.length === 0) return null;
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full mx-auto">
            {DASHBOARD_CARDS.map((config, cardIdx) => {
                const card = cardsData.find((c) => c.label === config.label);
                const items = card?.summary || card?.data || [];
                const maxValue = items.length > 0 ? Math.max(...items.map((item) => item.count || 0)) : 0;
                const bulletColor = CARD_COLORS[cardIdx % CARD_COLORS.length];
                const colSpanClass = config.width === 'full' ? 'md:col-span-2' : 'md:col-span-1';
                return (
                    <div key={config.label} className={`border border-gray-200 rounded-lg p-5 bg-white ${colSpanClass}`}>
                        <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-2">
                                <span className="inline-block w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: bulletColor }} />
                                <div className="flex items-start gap-1">
                                    <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wide">
                                        {config.label}
                                    </h3>
                                    {config.help_text && <InlineTooltip content={config.help_text} />}
                                </div>
                            </div>
                            <button onClick={() => onViewDetails(config.label)} className="text-xs text-primary hover:underline font-medium">
                                {viewDetailsBtn}
                            </button>
                        </div>
                        {items.length > 0 && (
                            <p className="text-xs text-gray-400 mb-3 ml-5">
                                {items.length.toLocaleString()} {totalRecordsText}
                            </p>
                        )}
                        {items.length === 0 ? (
                            <p className="text-sm text-gray-400">{noDataAvailable}</p>
                        ) : (
                            <div className="flex flex-col gap-2">
                                {items.slice(0, 5).map((item, idx) => {
                                    const value = item.count || 0;
                                    const barWidth = maxValue > 0 ? Math.max((value / maxValue) * 100, 5) : 5;
                                    return (
                                        <div key={idx} className="flex items-center gap-2 text-sm">
                                            <span className="text-gray-500 w-5 text-right shrink-0">{idx + 1}.</span>
                                            <div className="flex-1 min-w-0 relative">
                                                <div className="absolute inset-y-0 left-0 rounded" style={{ width: `${barWidth}%`, backgroundColor: `${bulletColor}22` }} />
                                                <span className="relative z-10 truncate block px-1 py-0.5 text-gray-800">{item.name}</span>
                                            </div>
                                            <span className="text-gray-600 font-medium shrink-0 tabular-nums">
                                                {value.toLocaleString()}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

const DetailView = ({ activeLabel, detailData, onBack, searchText, onSearch, gridApi, onGridReady, autoSizeStrategy, UI_CONTENT, loading }) => {
    const columns = useMemo(() => {
        const headers = UI_CONTENT.detail_table_headers || {};
        const cols = [
            { headerName: headers.index || '#', valueGetter: 'node.rowIndex + 1', width: 60, sortable: false, filter: false },
            { headerName: headers.name || 'Name', field: 'name', sortable: true, filter: true, resizable: true, flex: 1, minWidth: 200 },
        ];
        if (activeLabel === 'EXPERIENCES') {
            cols.push(
                { headerName: headers.created || 'Created', field: 'created', sortable: true, filter: true, resizable: true, width: 120 },
                { headerName: headers.approved || 'Approved', field: 'approved', sortable: true, filter: true, resizable: true, width: 120 },
                { headerName: headers.activated || 'Activated', field: 'activated', sortable: true, filter: true, resizable: true, width: 120 }
            );
        } else {
            const activeCardConfig = DASHBOARD_CARDS.find((c) => c.label === activeLabel);
            cols.push({ headerName: activeCardConfig?.count_header || headers.count || 'Count', field: 'count', sortable: true, filter: true, resizable: true, width: 150 });
        }
        return cols;
    }, [activeLabel, UI_CONTENT.detail_table_headers]);
    return (
        <div className="w-full">
            {/* Back button */}
            <div className="flex w-full my-3 mx-3 ">
                <button onClick={onBack} className="font-medium text-primary hover:underline flex items-center">
                    <svg className="w-4 h-4 mr-1" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 14 10">
                        <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 5H1m0 0 4 4M1 5l4-4" />
                    </svg>
                    {UI_CONTENT.back_btn_text}
                </button>
            </div>
            <div className="flex items-center justify-between mb-4">
                <h1 className="text-xl font-medium leading-tight tracking-tight text-gray-900 md:text-2xl ml-3">
                    {activeLabel}
                </h1>
            </div>
            <AgTableCard
                header={
                    <div className="flex items-center justify-between gap-3 flex-wrap w-full">
                        {detailData.length > 0 ? (
                            <div className="w-full md:w-80">
                                <Input
                                    value={searchText}
                                    onChange={onSearch}
                                    prefixIcon={<Search className="w-4 h-4 text-stride-muted" />}
                                    placeholder={UI_CONTENT.search_placeholder}
                                    clearable
                                />
                            </div>
                        ) : <div />}
                        {detailData.length > 0 && (
                            <CSVLink
                                data={detailData}
                                filename={`product-analytics-${activeLabel.replace(/\s+/g, '-').toLowerCase()}-${new Date().getTime()}.csv`}
                            >
                                <PrimaryButton className="whitespace-nowrap w-full md:w-auto">
                                    {UI_CONTENT.download_csv_btn}
                                    <svg className="w-2.5 h-2.5 ms-3 ml-2 inline-block" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 10 6">
                                        <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m1 1 4 4 4-4" />
                                    </svg>
                                </PrimaryButton>
                            </CSVLink>
                        )}
                    </div>
                }
                tableId="product-analytics-detail-table"
                loading={loading}
                emptyMessage={UI_CONTENT.no_data_available}
                rowData={detailData}
                columnDefs={columns}
                defaultColDef={{ sortable: true, filter: true, resizable: true }}
                autoSizeStrategy={autoSizeStrategy}
                onGridReady={onGridReady}
            />
        </div>
    );
};
// ── Org Accordion Row ─────────────────────────────────────────────────────────
const OrgAccordion = ({ orgData, UI_CONTENT, onViewDetails }) => {
    const [open, setOpen] = useState(false);
    const totalActivity = orgData.cards.reduce((sum, c) => sum + (c.total || 0), 0);
    return (
        <div className="border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm mb-4">
            {/* Header */}
            <button
                id={`org-accordion-${orgData.orgId}`}
                onClick={() => setOpen((o) => !o)}
                className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50 transition-colors"
            >
                <div className="flex items-center gap-4">
                    <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                        <span className="text-sm font-black text-primary uppercase">
                            {(orgData.orgName || '?').charAt(0)}
                        </span>
                    </div>
                    <div className="text-left w-48 shrink-0">
                        <p className="font-bold text-gray-800 text-sm capitalize truncate" title={orgData.orgName}>{orgData.orgName}</p>
                        <p className="text-xs text-gray-400">{totalActivity.toLocaleString()} total events</p>
                    </div>
                    {/* Summary Pills */}
                    <div className="hidden sm:flex items-center gap-2 ml-4 border-l border-gray-200 pl-4">
                        {orgData.cards.map((card, i) => {
                            let pillLabel = card.label;
                            if (card.label === 'USER LOGINS') pillLabel = 'USERS';
                            if (card.label === 'MOST USED FUNCTIONS') pillLabel = 'FUNCTIONS';
                            return (
                                <span
                                    key={card.label}
                                    className="text-xs font-semibold px-2 py-0.5 rounded-full"
                                    style={{ backgroundColor: `${CARD_COLORS[i]}22`, color: CARD_COLORS[i] }}
                                >
                                    {pillLabel}: {card.total.toLocaleString()}
                                </span>
                            );
                        })}
                    </div>
                </div>
                <svg
                    className={`w-4 h-4 text-gray-400 shrink-0 transition-transform duration-300 ${open ? 'rotate-180' : ''}`}
                    fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
            </button>
            {/* Body — same card grid */}
            {open && (
                <div className="px-5 pb-5 border-t border-gray-100 pt-4">
                    <CardGrid
                        cardsData={orgData.cards}
                        noDataMsg={UI_CONTENT.no_data_msg}
                        noDataAvailable={UI_CONTENT.no_data_available}
                        viewDetailsBtn={UI_CONTENT.view_details_btn}
                        totalRecordsText={UI_CONTENT.total_records_text}
                        onViewDetails={(label) => onViewDetails(orgData, label)}
                    />
                </div>
            )}
        </div>
    );
};
// ── Main Component ────────────────────────────────────────────────────────────
const ProductAnalyticsDashboard = () => {
    const { userData } = useUserContext();
    const { content } = useContentfulContext();
    const UI_CONTENT = content?.ANALYTICS_DASHBOARD_CONTENT || {
        start_date_label: 'Start Date',
        end_date_label: 'End Date',
        no_data_msg: 'No usage data found for the selected date range.',
        no_data_available: 'No data available.',
        back_btn_text: 'Back to Dashboard',
        download_csv_btn: 'Download CSV',
        search_placeholder: 'Search...',
        total_records_text: 'total records',
        view_details_btn: 'View Details →',
    };
    // ── State ─────────────────────────────────────────────────────────────────
    const [mode, setMode] = useState('all'); // 'all' | 'org'
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    // All-orgs data
    const [allCardsData, setAllCardsData] = useState([]);
    const [loadingAll, setLoadingAll] = useState(false);
    // Org-specific data
    const [orgsData, setOrgsData] = useState([]);
    const [loadingOrgs, setLoadingOrgs] = useState(false);
    const [error, setError] = useState('');
    const [hasSubmitted, setHasSubmitted] = useState(false);
    // Detail view state
    // activeView: null | { type: 'all', label } | { type: 'org', orgData, label }
    const [activeView, setActiveView] = useState(null);
    const [searchText, setSearchText] = useState('');
    const [gridApi, setGridApi] = useState(null);
    const autoSizeStrategy = useMemo(() => ({
        type: window.innerWidth < 1024 ? 'fitCellContents' : 'fitGridWidth',
        defaultMinWidth: 100,
    }), []);
    // ── Fetch All ─────────────────────────────────────────────────────────────
    const fetchAll = useCallback(async (start, end) => {
        if (!start || !end || !userData?.token) return;
        setLoadingAll(true);
        setError('');
        try {
            const result = await getAnalyticsEventsData(
                userData.token,
                userData?.selectedOrganization?.org_token,
                start, end,
                true // allOrgs=true
            );
            setAllCardsData(result?.data || []);
        } catch (err) {
            setError(err?.message || 'Failed to fetch analytics data.');
        } finally {
            setLoadingAll(false);
        }
    }, [userData?.token, userData?.selectedOrganization?.org_token]);
    // ── Fetch Orgs ────────────────────────────────────────────────────────────
    const fetchOrgs = useCallback(async (start, end) => {
        if (!start || !end || !userData?.token) return;
        setLoadingOrgs(true);
        setError('');
        try {
            const result = await getProductAnalyticsData(
                userData.token,
                userData?.selectedOrganization?.org_token,
                start, end
            );
            setOrgsData(result?.data || []);
        } catch (err) {
            setError(err?.message || 'Failed to fetch per-org analytics data.');
        } finally {
            setLoadingOrgs(false);
        }
    }, [userData?.token, userData?.selectedOrganization?.org_token]);
    // ── Init ──────────────────────────────────────────────────────────────────
    useEffect(() => {
        const end = new Date();
        const start = new Date();
        start.setDate(end.getDate() - 30);
        const s = start.toISOString().split('T')[0];
        const e = end.toISOString().split('T')[0];
        setStartDate(s);
        setEndDate(e);
        setHasSubmitted(true);
        fetchAll(s, e);
        fetchOrgs(s, e);
    }, [userData?.token]);
    // ── Date Change ───────────────────────────────────────────────────────────
    const handleDateChange = (start, end) => {
        setStartDate(start);
        setEndDate(end);
        setActiveView(null);
        setSearchText('');
        setHasSubmitted(true);
        fetchAll(start, end);
        fetchOrgs(start, end);
    };
    // ── View Details Handlers ─────────────────────────────────────────────────
    const handleViewDetailsAll = (label) => {
        setActiveView({ type: 'all', label });
        setSearchText('');
    };
    const handleViewDetailsOrg = (orgData, label) => {
        setActiveView({ type: 'org', orgData, label });
        setSearchText('');
    };
    const handleBack = () => {
        setActiveView(null);
        setSearchText('');
    };
    const handleSearch = (e) => {
        setSearchText(e.target.value);
        gridApi?.setQuickFilter(e.target.value);
    };
    const onGridReady = (params) => setGridApi(params.api);
    // ── Resolve detail data ───────────────────────────────────────────────────
    const detailData = useMemo(() => {
        if (!activeView) return [];
        const label = activeView.label;
        if (activeView.type === 'all') {
            const card = allCardsData.find((c) => c.label === label);
            return card?.data || [];
        } else {
            const card = activeView.orgData?.cards?.find((c) => c.label === label);
            return card?.data || [];
        }
    }, [activeView, allCardsData, orgsData]);
    const loading = mode === 'all' ? loadingAll : loadingOrgs;
    return (
        <div className="w-full py-4 px-8">
            {/* Top bar: Toggle + DatePicker */}
            {!activeView && (
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6 w-full">
                    {/* Toggle */}
                    <div className="flex bg-gray-100 rounded-xl p-1 gap-1">
                        <button
                            id="product-analytics-all-toggle"
                            onClick={() => { setMode('all'); setActiveView(null); }}
                            className={`px-4 py-1.5 rounded-lg text-sm font-semibold transition-all duration-200 ${mode === 'all' ? 'bg-white text-primary shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                        >
                            All
                        </button>
                        <button
                            id="product-analytics-org-toggle"
                            onClick={() => { setMode('org'); setActiveView(null); }}
                            className={`px-4 py-1.5 rounded-lg text-sm font-semibold transition-all duration-200 ${mode === 'org' ? 'bg-white text-primary shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                        >
                            Organisation Specific
                        </button>
                    </div>

                    <div className='pt-2'>
                    <DateRangePicker
                        startDate={startDate}
                        endDate={endDate}
                        onDateChange={handleDateChange}
                        label={`${UI_CONTENT.start_date_label} & ${UI_CONTENT.end_date_label}`}
                        allowFuture={false}
                    />
                    </div>
                </div>
            )}
            {/* Error */}
            {error && <h3 className="text-center text-red-500 w-full mb-4">{error}</h3>}
            {/* ── DETAIL VIEW ── */}
            {activeView && !loading && (
                <DetailView
                    activeLabel={activeView.label}
                    detailData={detailData}
                    onBack={handleBack}
                    searchText={searchText}
                    onSearch={handleSearch}
                    gridApi={gridApi}
                    onGridReady={onGridReady}
                    autoSizeStrategy={autoSizeStrategy}
                    UI_CONTENT={UI_CONTENT}
                    loading={loading}
                />
            )}
            {/* ── CARDS VIEW ── */}
            {!activeView && (
                <>
                    {/* Loading shimmer */}
                    {loading && <LoadingShimmer />}
                    {/* No data */}
                    {!loading && !error && hasSubmitted && mode === 'all' && allCardsData.length === 0 && (
                        <h3 className="text-center w-full">{UI_CONTENT.no_data_msg}</h3>
                    )}
                    {!loading && !error && hasSubmitted && mode === 'org' && orgsData.length === 0 && (
                        <h3 className="text-center w-full">{UI_CONTENT.no_data_msg}</h3>
                    )}
                    {/* ── ALL mode ── */}
                    {!loading && !error && mode === 'all' && allCardsData.length > 0 && (
                        <CardGrid
                            cardsData={allCardsData}
                            noDataMsg={UI_CONTENT.no_data_msg}
                            noDataAvailable={UI_CONTENT.no_data_available}
                            viewDetailsBtn={UI_CONTENT.view_details_btn}
                            totalRecordsText={UI_CONTENT.total_records_text}
                            onViewDetails={handleViewDetailsAll}
                        />
                    )}
                    {/* ── ORG mode ── */}
                    {!loading && !error && mode === 'org' && orgsData.length > 0 && (
                        <div className="mt-2">
                            {orgsData.map((org) => (
                                <OrgAccordion
                                    key={org.orgId}
                                    orgData={org}
                                    UI_CONTENT={UI_CONTENT}
                                    onViewDetails={handleViewDetailsOrg}
                                />
                            ))}
                        </div>
                    )}
                </>
            )}
        </div>
    );
};
export default ProductAnalyticsDashboard;